import React, { createContext, useContext, useState } from 'react';

const LanguageContext = createContext(null);

const translations = {
  en: {
    dir: 'ltr',
    // Auth
    clinicName: 'Younes Dental Clinic',
    adminPortal: 'Admin Portal - Younes Dental',
    welcomeBack: 'Welcome Back',
    signInSubtitle: 'Sign in to access your patient portal',
    email: 'Email',
    emailPlaceholder: 'your@email.com',
    password: 'Password',
    signIn: 'Sign In',
    signingIn: 'Signing in...',
    noAccount: "Don't have an account?",
    registerHere: 'Register here',
    createAccount: 'Create Account',
    registerSubtitle: 'Register to access your patient portal',
    firstName: 'First Name',
    lastName: 'Last Name',
    phone: 'Phone Number',
    dateOfBirth: 'Date of Birth',
    confirmPassword: 'Confirm Password',
    creatingAccount: 'Creating account...',
    haveAccount: 'Already have an account?',
    signInLink: 'Sign in',
    passwordsMismatch: 'Passwords do not match',
    passwordTooShort: 'Password must be at least 6 characters',
    loginSuccess: 'Login successful!',
    registerSuccess: 'Registration successful!',
    loginFailed: 'Login failed. Please check your credentials.',
    registerFailed: 'Registration failed. Please try again.',
    // Sidebar - Portal
    navDashboard: 'Dashboard',
    navTreatmentHistory: 'Treatment History',
    navPaymentRecords: 'Payment Records',
    navBookAppointment: 'Book Appointment',
    navMedicalHistory: 'Medical History',
    navMyScans: 'My Scans',
    navMedicalTests: 'Medical Tests',
    // Sidebar - Admin
    navPatients: 'Patients',
    navTreatments: 'Treatments',
    navPayments: 'Payments',
    navAppointments: 'Appointments',
    navScans: 'Scans',
    navNotifications: 'Notifications',
    // Dashboard
    welcomeUser: 'Welcome back,',
    dashboardSubtitle: "Here's an overview of your dental health records",
    completeMedicalHistory: 'Complete Your Medical History',
    completeMedicalHistoryDesc: 'Please fill out your medical history form. This is important for your treatment safety.',
    fillNow: 'Fill Now',
    medicalHistoryComplete: 'Medical History Complete',
    medicalHistoryOnFile: 'Your medical information is on file. Click to view.',
    newAppointmentProposal: 'New Appointment Proposal',
    clinicSuggestedTime: 'The clinic has suggested a different time for your appointment:',
    accept: 'Accept',
    decline: 'Decline',
    upcomingAppointments: 'Upcoming Appointments',
    scheduled: 'Scheduled',
    statUpcomingAppointments: 'Upcoming Appointments',
    statTotalTreatments: 'Total Treatments',
    statOutstandingBalance: 'Outstanding Balance',
    statLastVisit: 'Last Visit',
    quickActions: 'Quick Actions',
    viewTreatmentHistory: 'View Treatment History',
    viewTreatmentHistoryDesc: 'See all your past treatments and procedures',
    viewPaymentRecords: 'View Payment Records',
    viewPaymentRecordsDesc: 'Check your billing and payment history',
    bookNewAppointment: 'Book New Appointment',
    bookNewAppointmentDesc: 'Schedule your next dental visit',
    needHelp: 'Need Help?',
    needHelpDesc: 'Our team is here to assist you with any questions about your treatment or appointments.',
    callUs: 'Call Us',
    requestAppointment: 'Request an Appointment',
    preferredDate: 'Preferred Date',
    preferredTime: 'Preferred Time',
    notesOptional: 'Notes (optional)',
    sendRequest: 'Send Request',
    sending: 'Sending...',
    note: 'Note',
    // Treatment History
    treatmentHistory: 'Treatment History',
    treatmentHistorySubtitle: 'View all your past dental treatments and procedures',
    yourDentalChart: 'Your Dental Chart',
    teethHighlighted: 'Teeth highlighted in orange have been treated. Hover over a tooth to see details.',
    noTreatmentHistory: 'No Treatment History',
    noTreatmentHistoryDesc: "You don't have any recorded treatments yet.",
    date: 'Date',
    treatmentType: 'Treatment Type',
    doctor: 'Doctor',
    teeth: 'Teeth',
    notes: 'Notes',
    cost: 'Cost',
    // Payment Records
    paymentRecords: 'Payment Records',
    paymentRecordsSubtitle: 'View and manage your billing history',
    totalPaid: 'Total Paid',
    outstandingBalance: 'Outstanding Balance',
    noPaymentRecords: 'No Payment Records',
    noPaymentRecordsDesc: "You don't have any payment records yet.",
    invoiceNum: 'Invoice #',
    description: 'Description',
    status: 'Status',
    amount: 'Amount',
    invoice: 'Invoice',
    download: 'Download',
    paid: 'Paid',
    pending: 'Pending',
    overdue: 'Overdue',
    // Book Appointment
    bookAppointment: 'Book an Appointment',
    bookAppointmentSubtitle: 'Request a new appointment with our clinic',
    newRequest: 'New Request',
    myRequests: 'My Requests',
    noRequestsYet: 'No requests yet',
    clinicProposed: 'Clinic proposed:',
    at: 'at',
    statusPending: 'Pending',
    statusApproved: 'Approved',
    statusDenied: 'Denied',
    statusCounterProposed: 'Counter-Proposed',
    statusConfirmed: 'Confirmed',
    statusDeclined: 'Declined',
    // Medical History
    medicalHistory: 'Medical History',
    medicalHistoryViewSubtitle: 'Your medical information on file',
    medicalHistoryEditSubtitle: 'Please fill out your medical information accurately',
    submittedOn: 'Submitted on',
    requestEdit: 'Request Edit',
    editRequested: 'Edit requested',
    medicalConditions: 'Medical Conditions',
    selectConditions: 'Select any conditions you have',
    noneReported: 'None reported',
    otherConditions: 'Other medical conditions',
    underPhysicianCare: "Are you currently under a physician's care?",
    medications: 'Medications',
    takingMedications: 'Are you taking any medications?',
    listMedications: 'List medications (e.g., Warfarin, Aspirin, Insulin)',
    allergies: 'Allergies',
    haveAllergies: 'Do you have any medication allergies?',
    listAllergies: 'List allergies (e.g., Penicillin)',
    allergyAnesthesia: 'Allergy to local anesthesia?',
    dentalHistory: 'Dental History',
    lastDentalVisit: 'Date of last dental visit',
    previousTreatments: 'Previous dental treatments',
    oralHealthHabits: 'Oral Health Habits',
    brushingFrequency: 'How often do you brush?',
    twiceDaily: 'Twice daily',
    onceDaily: 'Once daily',
    occasionally: 'Occasionally',
    doYouFloss: 'Do you floss?',
    doYouSmoke: 'Do you smoke?',
    smokingWarning: 'Smoking increases risk of Periodontal Disease and Oral Cancer.',
    consent: 'Consent',
    consentText: 'I confirm that the information provided is accurate to the best of my knowledge. I understand this information will be used for my dental treatment planning.',
    submitMedicalHistory: 'Submit Medical History',
    saving: 'Saving...',
    yes: 'Yes',
    no: 'No',
    // Scans
    myScans: 'My Scans',
    myScansSubtitle: 'View and upload dental X-rays and scans',
    uploadNewScan: 'Upload New Scan',
    scanType: 'Scan Type',
    xray: 'X-Ray',
    panoramic: 'Panoramic',
    cbct: 'CBCT',
    other: 'Other',
    descriptionOptional: 'Description (optional)',
    clickToUploadScan: 'Click to upload scan image',
    uploading: 'Uploading...',
    noScansYet: 'No scans uploaded yet',
    view: 'View',
    // Medical Tests
    medicalTests: 'Medical Tests',
    medicalTestsSubtitle: 'Upload and manage your medical test results',
    uploadNewTest: 'Upload New Test',
    testName: 'Test Name',
    testDate: 'Test Date',
    clickToUploadTest: 'Click to upload test result (PDF, image)',
    noTestsYet: 'No medical tests uploaded yet',
    // Admin pages
    treatmentManagement: 'Treatment Management',
    manageTreatments: 'Manage all patient treatments',
    addTreatment: 'Add Treatment',
    editTreatment: 'Edit Treatment',
    addNewTreatment: 'Add New Treatment',
    patient: 'Patient',
    selectPatient: 'Select Patient',
    selectDoctor: 'Select Doctor',
    selectTeeth: 'Select Teeth (click to toggle)',
    cancel: 'Cancel',
    updateTreatment: 'Update Treatment',
    search: 'Search...',
    actions: 'Actions',
    noResults: 'No results',
    noTreatmentsYet: 'No treatments yet',
    paymentManagement: 'Payment Management',
    managePayments: 'Manage all patient payments and invoices',
    addPayment: 'Add Payment',
    editPayment: 'Edit Payment',
    addNewPayment: 'Add New Payment',
    forTreatment: 'For Treatment',
    selectTreatmentOptional: 'Select Treatment (optional)',
    invoiceNumber: 'Invoice Number',
    autoGenerated: 'Auto-generated if empty',
    updatePayment: 'Update Payment',
    noPaymentsYet: 'No payments yet',
    // Admin Scans & Tests
    patientXrays: 'Patient X-Rays & Scans',
    patientXraysSubtitle: 'View all patient dental scans and X-rays. Upload new scans for patients.',
    upload: 'Upload',
    searchScans: 'Search by patient name, type, description...',
    noScansUploadedYet: 'No scans uploaded yet',
    patientMedicalTests: 'Patient Medical Tests',
    patientMedicalTestsSubtitle: 'View all patient medical test results. Upload new tests for patients.',
    searchTests: 'Search by patient, test name, description...',
    noTestsUploadedYet: 'No medical tests uploaded yet',
    uploadedBy: 'Uploaded by',
    uploadTestResult: 'Upload Test Result',
    uploadPdfImage: 'Upload (PDF, image)',
  },
  ar: {
    dir: 'rtl',
    clinicName: 'عيادة يونس لطب الأسنان',
    adminPortal: 'لوحة الإدارة - عيادة يونس',
    welcomeBack: 'مرحباً بعودتك',
    signInSubtitle: 'سجّل الدخول للوصول إلى بوابة المريض',
    email: 'البريد الإلكتروني',
    emailPlaceholder: 'your@email.com',
    password: 'كلمة المرور',
    signIn: 'تسجيل الدخول',
    signingIn: 'جارٍ تسجيل الدخول...',
    noAccount: 'ليس لديك حساب؟',
    registerHere: 'سجّل هنا',
    createAccount: 'إنشاء حساب',
    registerSubtitle: 'سجّل للوصول إلى بوابة المريض',
    firstName: 'الاسم الأول',
    lastName: 'اسم العائلة',
    phone: 'رقم الهاتف',
    dateOfBirth: 'تاريخ الميلاد',
    confirmPassword: 'تأكيد كلمة المرور',
    creatingAccount: 'جارٍ إنشاء الحساب...',
    haveAccount: 'لديك حساب بالفعل؟',
    signInLink: 'تسجيل الدخول',
    passwordsMismatch: 'كلمات المرور غير متطابقة',
    passwordTooShort: 'يجب أن تتكون كلمة المرور من 6 أحرف على الأقل',
    loginSuccess: 'تم تسجيل الدخول بنجاح!',
    registerSuccess: 'تم التسجيل بنجاح!',
    loginFailed: 'فشل تسجيل الدخول. يرجى التحقق من بياناتك.',
    registerFailed: 'فشل التسجيل. يرجى المحاولة مرة أخرى.',
    navDashboard: 'لوحة التحكم',
    navTreatmentHistory: 'سجل العلاج',
    navPaymentRecords: 'سجل المدفوعات',
    navBookAppointment: 'حجز موعد',
    navMedicalHistory: 'التاريخ الطبي',
    navMyScans: 'صوري',
    navMedicalTests: 'الفحوصات الطبية',
    navPatients: 'المرضى',
    navTreatments: 'العلاجات',
    navPayments: 'المدفوعات',
    navAppointments: 'المواعيد',
    navScans: 'الصور',
    navNotifications: 'الإشعارات',
    welcomeUser: 'مرحباً بعودتك،',
    dashboardSubtitle: 'إليك نظرة عامة على سجلاتك الصحية',
    completeMedicalHistory: 'أكمل تاريخك الطبي',
    completeMedicalHistoryDesc: 'يرجى ملء نموذج التاريخ الطبي. هذا مهم لسلامة علاجك.',
    fillNow: 'املأ الآن',
    medicalHistoryComplete: 'التاريخ الطبي مكتمل',
    medicalHistoryOnFile: 'معلوماتك الطبية مسجلة. انقر للعرض.',
    newAppointmentProposal: 'اقتراح موعد جديد',
    clinicSuggestedTime: 'اقترحت العيادة وقتاً مختلفاً لموعدك:',
    accept: 'قبول',
    decline: 'رفض',
    upcomingAppointments: 'المواعيد القادمة',
    scheduled: 'مجدول',
    statUpcomingAppointments: 'المواعيد القادمة',
    statTotalTreatments: 'إجمالي العلاجات',
    statOutstandingBalance: 'الرصيد المستحق',
    statLastVisit: 'آخر زيارة',
    quickActions: 'إجراءات سريعة',
    viewTreatmentHistory: 'عرض سجل العلاج',
    viewTreatmentHistoryDesc: 'اطلع على جميع علاجاتك السابقة',
    viewPaymentRecords: 'عرض سجل المدفوعات',
    viewPaymentRecordsDesc: 'تحقق من سجل الفواتير والمدفوعات',
    bookNewAppointment: 'حجز موعد جديد',
    bookNewAppointmentDesc: 'حدد موعد زيارتك القادمة',
    needHelp: 'تحتاج مساعدة؟',
    needHelpDesc: 'فريقنا هنا لمساعدتك في أي أسئلة حول علاجك أو مواعيدك.',
    callUs: 'اتصل بنا',
    requestAppointment: 'طلب موعد',
    preferredDate: 'التاريخ المفضل',
    preferredTime: 'الوقت المفضل',
    notesOptional: 'ملاحظات (اختياري)',
    sendRequest: 'إرسال الطلب',
    sending: 'جارٍ الإرسال...',
    note: 'ملاحظة',
    treatmentHistory: 'سجل العلاج',
    treatmentHistorySubtitle: 'اطلع على جميع علاجات الأسنان السابقة',
    yourDentalChart: 'مخطط أسنانك',
    teethHighlighted: 'الأسنان المظللة بالبرتقالي تم علاجها. مرر الفأرة لمعرفة التفاصيل.',
    noTreatmentHistory: 'لا يوجد سجل علاج',
    noTreatmentHistoryDesc: 'ليس لديك أي علاجات مسجلة بعد.',
    date: 'التاريخ',
    treatmentType: 'نوع العلاج',
    doctor: 'الطبيب',
    teeth: 'الأسنان',
    notes: 'ملاحظات',
    cost: 'التكلفة',
    paymentRecords: 'سجل المدفوعات',
    paymentRecordsSubtitle: 'عرض وإدارة سجل الفواتير',
    totalPaid: 'إجمالي المدفوع',
    outstandingBalance: 'الرصيد المستحق',
    noPaymentRecords: 'لا توجد سجلات دفع',
    noPaymentRecordsDesc: 'ليس لديك أي سجلات دفع بعد.',
    invoiceNum: 'رقم الفاتورة',
    description: 'الوصف',
    status: 'الحالة',
    amount: 'المبلغ',
    invoice: 'فاتورة',
    download: 'تحميل',
    paid: 'مدفوع',
    pending: 'قيد الانتظار',
    overdue: 'متأخر',
    bookAppointment: 'حجز موعد',
    bookAppointmentSubtitle: 'اطلب موعداً جديداً مع عيادتنا',
    newRequest: 'طلب جديد',
    myRequests: 'طلباتي',
    noRequestsYet: 'لا توجد طلبات بعد',
    clinicProposed: 'اقترحت العيادة:',
    at: 'في',
    statusPending: 'قيد الانتظار',
    statusApproved: 'موافق عليه',
    statusDenied: 'مرفوض',
    statusCounterProposed: 'اقتراح بديل',
    statusConfirmed: 'مؤكد',
    statusDeclined: 'مرفوض',
    medicalHistory: 'التاريخ الطبي',
    medicalHistoryViewSubtitle: 'معلوماتك الطبية المسجلة',
    medicalHistoryEditSubtitle: 'يرجى ملء معلوماتك الطبية بدقة',
    submittedOn: 'تم التقديم في',
    requestEdit: 'طلب تعديل',
    editRequested: 'تم طلب التعديل',
    medicalConditions: 'الحالات الطبية',
    selectConditions: 'اختر أي حالات لديك',
    noneReported: 'لم يتم الإبلاغ عن أي حالة',
    otherConditions: 'حالات طبية أخرى',
    underPhysicianCare: 'هل أنت حالياً تحت رعاية طبيب؟',
    medications: 'الأدوية',
    takingMedications: 'هل تتناول أي أدوية؟',
    listMedications: 'اذكر الأدوية (مثل: وارفارين، أسبرين، إنسولين)',
    allergies: 'الحساسية',
    haveAllergies: 'هل لديك حساسية من أي أدوية؟',
    listAllergies: 'اذكر أنواع الحساسية (مثل: بنسلين)',
    allergyAnesthesia: 'حساسية من التخدير الموضعي؟',
    dentalHistory: 'تاريخ الأسنان',
    lastDentalVisit: 'تاريخ آخر زيارة لطبيب الأسنان',
    previousTreatments: 'العلاجات السابقة',
    oralHealthHabits: 'عادات صحة الفم',
    brushingFrequency: 'كم مرة تنظف أسنانك؟',
    twiceDaily: 'مرتين يومياً',
    onceDaily: 'مرة يومياً',
    occasionally: 'أحياناً',
    doYouFloss: 'هل تستخدم خيط الأسنان؟',
    doYouSmoke: 'هل تدخن؟',
    smokingWarning: 'التدخين يزيد من خطر أمراض اللثة وسرطان الفم.',
    consent: 'الموافقة',
    consentText: 'أؤكد أن المعلومات المقدمة دقيقة على حد علمي. أفهم أن هذه المعلومات ستستخدم لتخطيط علاج أسناني.',
    submitMedicalHistory: 'تقديم التاريخ الطبي',
    saving: 'جارٍ الحفظ...',
    yes: 'نعم',
    no: 'لا',
    myScans: 'صوري',
    myScansSubtitle: 'عرض وتحميل صور الأشعة والفحوصات',
    uploadNewScan: 'تحميل صورة جديدة',
    scanType: 'نوع الصورة',
    xray: 'أشعة سينية',
    panoramic: 'بانوراما',
    cbct: 'CBCT',
    other: 'أخرى',
    descriptionOptional: 'وصف (اختياري)',
    clickToUploadScan: 'انقر لتحميل صورة الفحص',
    uploading: 'جارٍ التحميل...',
    noScansYet: 'لم يتم تحميل أي صور بعد',
    view: 'عرض',
    medicalTests: 'الفحوصات الطبية',
    medicalTestsSubtitle: 'تحميل وإدارة نتائج الفحوصات الطبية',
    uploadNewTest: 'تحميل فحص جديد',
    testName: 'اسم الفحص',
    testDate: 'تاريخ الفحص',
    clickToUploadTest: 'انقر لتحميل نتيجة الفحص (PDF أو صورة)',
    noTestsYet: 'لم يتم تحميل أي فحوصات بعد',
    treatmentManagement: 'إدارة العلاجات',
    manageTreatments: 'إدارة جميع علاجات المرضى',
    addTreatment: 'إضافة علاج',
    editTreatment: 'تعديل العلاج',
    addNewTreatment: 'إضافة علاج جديد',
    patient: 'المريض',
    selectPatient: 'اختر المريض',
    selectDoctor: 'اختر الطبيب',
    selectTeeth: 'اختر الأسنان (انقر للتبديل)',
    cancel: 'إلغاء',
    updateTreatment: 'تحديث العلاج',
    search: 'بحث...',
    actions: 'الإجراءات',
    noResults: 'لا توجد نتائج',
    noTreatmentsYet: 'لا توجد علاجات بعد',
    paymentManagement: 'إدارة المدفوعات',
    managePayments: 'إدارة جميع مدفوعات وفواتير المرضى',
    addPayment: 'إضافة دفعة',
    editPayment: 'تعديل الدفعة',
    addNewPayment: 'إضافة دفعة جديدة',
    forTreatment: 'للعلاج',
    selectTreatmentOptional: 'اختر العلاج (اختياري)',
    invoiceNumber: 'رقم الفاتورة',
    autoGenerated: 'يتم إنشاؤه تلقائياً إذا ترك فارغاً',
    updatePayment: 'تحديث الدفعة',
    noPaymentsYet: 'لا توجد مدفوعات بعد',
    patientXrays: 'صور الأشعة للمرضى',
    patientXraysSubtitle: 'عرض جميع صور الأشعة والفحوصات. تحميل صور جديدة.',
    upload: 'تحميل',
    searchScans: 'البحث باسم المريض أو النوع أو الوصف...',
    noScansUploadedYet: 'لم يتم تحميل أي صور بعد',
    patientMedicalTests: 'الفحوصات الطبية للمرضى',
    patientMedicalTestsSubtitle: 'عرض جميع نتائج الفحوصات الطبية. تحميل فحوصات جديدة.',
    searchTests: 'البحث باسم المريض أو اسم الفحص أو الوصف...',
    noTestsUploadedYet: 'لم يتم تحميل أي فحوصات بعد',
    uploadedBy: 'تم التحميل بواسطة',
    uploadTestResult: 'تحميل نتيجة فحص',
    uploadPdfImage: 'تحميل (PDF أو صورة)',
  },
  fr: {
    dir: 'ltr',
    clinicName: 'Clinique Dentaire Younes',
    adminPortal: 'Portail Admin - Clinique Younes',
    welcomeBack: 'Bienvenue',
    signInSubtitle: 'Connectez-vous pour accéder à votre portail patient',
    email: 'E-mail',
    emailPlaceholder: 'votre@email.com',
    password: 'Mot de passe',
    signIn: 'Se connecter',
    signingIn: 'Connexion en cours...',
    noAccount: "Vous n'avez pas de compte ?",
    registerHere: "S'inscrire",
    createAccount: 'Créer un compte',
    registerSubtitle: 'Inscrivez-vous pour accéder à votre portail patient',
    firstName: 'Prénom',
    lastName: 'Nom',
    phone: 'Téléphone',
    dateOfBirth: 'Date de naissance',
    confirmPassword: 'Confirmer le mot de passe',
    creatingAccount: 'Création du compte...',
    haveAccount: 'Vous avez déjà un compte ?',
    signInLink: 'Se connecter',
    passwordsMismatch: 'Les mots de passe ne correspondent pas',
    passwordTooShort: 'Le mot de passe doit comporter au moins 6 caractères',
    loginSuccess: 'Connexion réussie !',
    registerSuccess: 'Inscription réussie !',
    loginFailed: 'Échec de la connexion. Vérifiez vos identifiants.',
    registerFailed: "Échec de l'inscription. Veuillez réessayer.",
    navDashboard: 'Tableau de bord',
    navTreatmentHistory: 'Historique des soins',
    navPaymentRecords: 'Historique des paiements',
    navBookAppointment: 'Prendre rendez-vous',
    navMedicalHistory: 'Antécédents médicaux',
    navMyScans: 'Mes radiographies',
    navMedicalTests: 'Examens médicaux',
    navPatients: 'Patients',
    navTreatments: 'Soins',
    navPayments: 'Paiements',
    navAppointments: 'Rendez-vous',
    navScans: 'Radiographies',
    navNotifications: 'Notifications',
    welcomeUser: 'Bienvenue,',
    dashboardSubtitle: "Voici un aperçu de vos dossiers de santé dentaire",
    completeMedicalHistory: 'Complétez vos antécédents médicaux',
    completeMedicalHistoryDesc: 'Veuillez remplir le formulaire. Ceci est important pour la sécurité de votre traitement.',
    fillNow: 'Remplir maintenant',
    medicalHistoryComplete: 'Antécédents médicaux complets',
    medicalHistoryOnFile: 'Vos informations médicales sont enregistrées. Cliquez pour voir.',
    newAppointmentProposal: 'Nouvelle proposition de rendez-vous',
    clinicSuggestedTime: 'La clinique a suggéré un autre horaire pour votre rendez-vous :',
    accept: 'Accepter',
    decline: 'Refuser',
    upcomingAppointments: 'Rendez-vous à venir',
    scheduled: 'Planifié',
    statUpcomingAppointments: 'Rendez-vous à venir',
    statTotalTreatments: 'Total des soins',
    statOutstandingBalance: 'Solde impayé',
    statLastVisit: 'Dernière visite',
    quickActions: 'Actions rapides',
    viewTreatmentHistory: "Voir l'historique des soins",
    viewTreatmentHistoryDesc: 'Consultez tous vos soins passés',
    viewPaymentRecords: 'Voir les paiements',
    viewPaymentRecordsDesc: 'Vérifiez votre historique de facturation',
    bookNewAppointment: 'Prendre un rendez-vous',
    bookNewAppointmentDesc: 'Planifiez votre prochaine visite',
    needHelp: "Besoin d'aide ?",
    needHelpDesc: "Notre équipe est là pour vous aider avec toute question sur votre traitement ou vos rendez-vous.",
    callUs: 'Appelez-nous',
    requestAppointment: 'Demander un rendez-vous',
    preferredDate: 'Date souhaitée',
    preferredTime: 'Heure souhaitée',
    notesOptional: 'Notes (facultatif)',
    sendRequest: 'Envoyer la demande',
    sending: 'Envoi en cours...',
    note: 'Note',
    treatmentHistory: 'Historique des soins',
    treatmentHistorySubtitle: 'Consultez tous vos soins dentaires passés',
    yourDentalChart: 'Votre schéma dentaire',
    teethHighlighted: 'Les dents en orange ont été traitées. Survolez pour voir les détails.',
    noTreatmentHistory: 'Aucun historique de soins',
    noTreatmentHistoryDesc: "Vous n'avez aucun soin enregistré.",
    date: 'Date',
    treatmentType: 'Type de soin',
    doctor: 'Médecin',
    teeth: 'Dents',
    notes: 'Notes',
    cost: 'Coût',
    paymentRecords: 'Historique des paiements',
    paymentRecordsSubtitle: 'Gérez votre historique de facturation',
    totalPaid: 'Total payé',
    outstandingBalance: 'Solde impayé',
    noPaymentRecords: 'Aucun paiement',
    noPaymentRecordsDesc: "Vous n'avez aucun enregistrement de paiement.",
    invoiceNum: 'N° facture',
    description: 'Description',
    status: 'Statut',
    amount: 'Montant',
    invoice: 'Facture',
    download: 'Télécharger',
    paid: 'Payé',
    pending: 'En attente',
    overdue: 'En retard',
    bookAppointment: 'Prendre rendez-vous',
    bookAppointmentSubtitle: 'Demandez un nouveau rendez-vous avec notre clinique',
    newRequest: 'Nouvelle demande',
    myRequests: 'Mes demandes',
    noRequestsYet: 'Aucune demande',
    clinicProposed: 'La clinique a proposé :',
    at: 'à',
    statusPending: 'En attente',
    statusApproved: 'Approuvé',
    statusDenied: 'Refusé',
    statusCounterProposed: 'Contre-proposition',
    statusConfirmed: 'Confirmé',
    statusDeclined: 'Décliné',
    medicalHistory: 'Antécédents médicaux',
    medicalHistoryViewSubtitle: 'Vos informations médicales enregistrées',
    medicalHistoryEditSubtitle: 'Veuillez remplir vos informations médicales avec précision',
    submittedOn: 'Soumis le',
    requestEdit: 'Demander une modification',
    editRequested: 'Modification demandée',
    medicalConditions: 'Conditions médicales',
    selectConditions: 'Sélectionnez vos conditions',
    noneReported: 'Aucune signalée',
    otherConditions: 'Autres conditions médicales',
    underPhysicianCare: 'Êtes-vous actuellement suivi par un médecin ?',
    medications: 'Médicaments',
    takingMedications: 'Prenez-vous des médicaments ?',
    listMedications: 'Listez les médicaments (ex: Warfarine, Aspirine, Insuline)',
    allergies: 'Allergies',
    haveAllergies: 'Avez-vous des allergies médicamenteuses ?',
    listAllergies: 'Listez les allergies (ex: Pénicilline)',
    allergyAnesthesia: "Allergie à l'anesthésie locale ?",
    dentalHistory: 'Historique dentaire',
    lastDentalVisit: 'Date de la dernière visite dentaire',
    previousTreatments: 'Soins dentaires précédents',
    oralHealthHabits: 'Habitudes de santé bucco-dentaire',
    brushingFrequency: 'Combien de fois vous brossez-vous les dents ?',
    twiceDaily: 'Deux fois par jour',
    onceDaily: 'Une fois par jour',
    occasionally: 'Occasionnellement',
    doYouFloss: 'Utilisez-vous du fil dentaire ?',
    doYouSmoke: 'Fumez-vous ?',
    smokingWarning: 'Le tabagisme augmente le risque de maladie parodontale et de cancer buccal.',
    consent: 'Consentement',
    consentText: "Je confirme que les informations fournies sont exactes. Je comprends qu'elles seront utilisées pour la planification de mon traitement dentaire.",
    submitMedicalHistory: 'Soumettre les antécédents',
    saving: 'Enregistrement...',
    yes: 'Oui',
    no: 'Non',
    myScans: 'Mes radiographies',
    myScansSubtitle: 'Voir et télécharger vos radiographies dentaires',
    uploadNewScan: 'Télécharger une nouvelle radiographie',
    scanType: 'Type de radiographie',
    xray: 'Radiographie',
    panoramic: 'Panoramique',
    cbct: 'CBCT',
    other: 'Autre',
    descriptionOptional: 'Description (facultatif)',
    clickToUploadScan: 'Cliquez pour télécharger une radiographie',
    uploading: 'Téléchargement...',
    noScansYet: 'Aucune radiographie téléchargée',
    view: 'Voir',
    medicalTests: 'Examens médicaux',
    medicalTestsSubtitle: 'Téléchargez et gérez vos résultats d\'examens',
    uploadNewTest: 'Télécharger un nouvel examen',
    testName: "Nom de l'examen",
    testDate: "Date de l'examen",
    clickToUploadTest: 'Cliquez pour télécharger (PDF, image)',
    noTestsYet: 'Aucun examen téléchargé',
    treatmentManagement: 'Gestion des soins',
    manageTreatments: 'Gérer tous les soins des patients',
    addTreatment: 'Ajouter un soin',
    editTreatment: 'Modifier le soin',
    addNewTreatment: 'Ajouter un nouveau soin',
    patient: 'Patient',
    selectPatient: 'Sélectionner un patient',
    selectDoctor: 'Sélectionner un médecin',
    selectTeeth: 'Sélectionner les dents (cliquez pour basculer)',
    cancel: 'Annuler',
    updateTreatment: 'Mettre à jour le soin',
    search: 'Rechercher...',
    actions: 'Actions',
    noResults: 'Aucun résultat',
    noTreatmentsYet: 'Aucun soin',
    paymentManagement: 'Gestion des paiements',
    managePayments: 'Gérer tous les paiements et factures',
    addPayment: 'Ajouter un paiement',
    editPayment: 'Modifier le paiement',
    addNewPayment: 'Ajouter un nouveau paiement',
    forTreatment: 'Pour le soin',
    selectTreatmentOptional: 'Sélectionner un soin (facultatif)',
    invoiceNumber: 'Numéro de facture',
    autoGenerated: 'Généré automatiquement si vide',
    updatePayment: 'Mettre à jour le paiement',
    noPaymentsYet: 'Aucun paiement',
    patientXrays: 'Radiographies des patients',
    patientXraysSubtitle: 'Voir toutes les radiographies. Télécharger de nouvelles radiographies.',
    upload: 'Télécharger',
    searchScans: 'Rechercher par nom, type, description...',
    noScansUploadedYet: 'Aucune radiographie téléchargée',
    patientMedicalTests: 'Examens médicaux des patients',
    patientMedicalTestsSubtitle: 'Voir tous les résultats. Télécharger de nouveaux examens.',
    searchTests: "Rechercher par patient, nom d'examen, description...",
    noTestsUploadedYet: 'Aucun examen téléchargé',
    uploadedBy: 'Téléchargé par',
    uploadTestResult: "Télécharger un résultat d'examen",
    uploadPdfImage: 'Télécharger (PDF, image)',
  },
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(localStorage.getItem('lang') || 'en');

  const setLanguage = (l) => {
    setLang(l);
    localStorage.setItem('lang', l);
  };

  const t = translations[lang] || translations.en;

  return (
    <LanguageContext.Provider value={{ lang, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within LanguageProvider');
  return context;
};
